import type { ComponentType } from "react";

import {
  ArrayIcon,
  Box,
  DollarIcon,
  ObjectIcon,
  Row,
  SparkleIcon,
  SplitIcon,
  TableIcon,
  Text,
  Tooltip,
  TraitIcon,
  VariableIcon,
} from "@hightouch/htui";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { MappingType } from "src/formkit/components/types";
import {
  formatStaticTraitLabel,
  formatValue,
  isJourneyVariableColumn,
  isAudienceTrait,
  isSplitColumn,
  NEW_STATIC_ICONS,
} from "src/formkit/components/utils";
import { resolveVariableScope } from "./variable-options";
import { variableValueLabel } from "./variable-labels";
import {
  JOURNEY_CONTEXT_VARIABLE_VALUE_PREFIX,
  type ColumnOption,
  useFormkitContext,
} from "src/formkit/components/formkit-context";
import { findBoostedFirstPartyColumn } from "./utils";

type ValueProps = Readonly<{
  id?: string;
  isDisabled?: boolean;
  placeholder: string | undefined;
  value: any;
  columnOptions?: ColumnOption[] | undefined;
  selectedOptionSemanticType: string | undefined;
}>;

/** The mapper trigger's display of a mapping value. */
export function Value({
  id,
  isDisabled,
  placeholder,
  value,
  columnOptions,
  selectedOptionSemanticType,
}: ValueProps) {
  const { model, columns: contextColumns, variableScope } = useFormkitContext();
  let Icon: ComponentType<any> = TableIcon;
  let backgroundColor: string | undefined = "gray.200";
  if (value.from?.type === MappingType.BOOSTED) {
    Icon = SparkleIconYellow;
    backgroundColor = undefined;
  }

  if (value.type === MappingType.STATIC) {
    Icon = NEW_STATIC_ICONS[value.valueType];
    backgroundColor = "teal.200";
  }

  if (value.type === MappingType.VARIABLE) {
    Icon = DollarIcon;
    backgroundColor = "warning.200";
  }

  if (value.type === MappingType.TEMPLATE) {
    Icon = ObjectIcon;
    backgroundColor = "blue.200";
  }

  if (value.type === MappingType.OBJECT) {
    Icon = ObjectIcon;
    backgroundColor = undefined;
  }

  if (value.type === MappingType.ARRAY) {
    Icon = ArrayIcon;
    backgroundColor = undefined;
  }

  if (
    isEmpty(value) ||
    (value.type === MappingType.STANDARD &&
      typeof value.from === "undefined") ||
    (value.type === MappingType.VARIABLE &&
      typeof value.variable === "undefined") ||
    (value.type === MappingType.STATIC && typeof value.value === "undefined") ||
    (value.type === MappingType.TEMPLATE &&
      typeof value.template === "undefined") ||
    (Object.keys(value).length === 1 && "to" in value)
  ) {
    return (
      <Box id={id} overflow="hidden">
        <Text
          color={isDisabled ? "text.disabled" : "text.tertiary"}
          whiteSpace="nowrap"
        >
          {placeholder}
        </Text>
      </Box>
    );
  }

  const isFieldBoosted = value.from?.type === MappingType.BOOSTED;
  const boostedFieldInModel = findBoostedFirstPartyColumn(
    model,
    selectedOptionSemanticType,
  );

  const boostedColumnAlias = model?.syncable_columns?.find((scc) => {
    return scc.column_reference.semanticType === selectedOptionSemanticType;
  });

  let formattedValue =
    value.type === MappingType.VARIABLE
      ? variableValueLabel({
          scope: resolveVariableScope({ variableScope, model }),
          variable: value.variable,
        })
      : formatValue(value, {
          prefixToRemove: JOURNEY_CONTEXT_VARIABLE_VALUE_PREFIX,
          columnOptions: columnOptions ?? contextColumns,
        });

  if (isAudienceTrait(value.from?.type)) {
    const additionalCol = model?.visual_query_filter?.additionalColumns?.find(
      (c) => c.alias === value.from.columnAlias,
    );
    if (additionalCol?.column?.type === "static_value") {
      formattedValue = formatStaticTraitLabel(
        value.from.columnAlias,
        String(additionalCol.column.value),
      );
    }
  }

  const component = (
    <Box
      id={id}
      alignItems="center"
      bg={backgroundColor}
      borderRadius="md"
      display="grid"
      gap={1}
      gridTemplateColumns="16px 1fr"
      height="100%"
      maxWidth="90%"
      px={2}
      sx={{
        span: {
          color: value.type === MappingType.OBJECT ? "teal.700" : "inherit",
        },
      }}
    >
      {isTrait(value.from) || isAudienceTrait(value.from?.type) ? (
        <Box as={TraitIcon} color="text.secondary" boxSize={4} ml={-0.5} />
      ) : isSplitColumn(value.from?.type) ? (
        <Box as={SplitIcon} color="text.secondary" boxSize={4} ml={-0.5} />
      ) : isJourneyVariableColumn(value.from) ? (
        <Box as={VariableIcon} color="text.secondary" boxSize={4} ml={-0.5} />
      ) : (
        <Box as={Icon} color="text.secondary" boxSize={4} ml={-0.5} />
      )}

      <Text
        isTruncated
        color="inherit"
        size={
          value.type === MappingType.OBJECT || value.type === MappingType.ARRAY
            ? "md"
            : "sm"
        }
      >
        {isFieldBoosted
          ? (boostedColumnAlias?.alias ?? formattedValue)
          : formattedValue}{" "}
        {isFieldBoosted && boostedFieldInModel && (
          <Text color="text.secondary" isTruncated={true}>
            ({boostedFieldInModel.name})
          </Text>
        )}
      </Text>
    </Box>
  );

  if (!isFieldBoosted) {
    return component;
  } else if (
    isFieldBoosted &&
    boostedFieldInModel &&
    boostedColumnAlias?.alias
  ) {
    return (
      <Tooltip
        message={
          <Row display="inline" textAlign="left">
            Field has been boosted to send
            <Box pl={1} display="inline" pr={0.5}>
              <SparkleIcon color="warning.400" />
            </Box>
            <Text color="warning.400">{boostedColumnAlias.alias}</Text> data
            found in Hightouch's identity graph in addition to the data in the{" "}
            <Text color="text.tertiary">{boostedFieldInModel.name}</Text> column
            of the model.
          </Row>
        }
      >
        {component}
      </Tooltip>
    );
  }

  return (
    <Tooltip
      message={
        <Row display="inline">
          Field has been boosted to send
          <Box pl={1} display="inline" pr={0.5}>
            <SparkleIcon color="warning.400" />
          </Box>
          <Text color="warning.400">{boostedColumnAlias?.alias || ""}</Text>{" "}
          data found in Hightouch’s identity graph.
        </Row>
      }
    >
      {component}
    </Tooltip>
  );
}

function isTrait(value: unknown): boolean {
  return get(value, "column.type") === "trait";
}

function SparkleIconYellow() {
  return (
    <Box display="flex" fontSize="16px" ml={-0.5}>
      <SparkleIcon color="warning.400" />
    </Box>
  );
}
